import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";
import { track } from "../lib/analytics";

const steps = [
  {
    n: "01",
    title: "Pick your seven",
    body: "Browse a curated pool and choose up to seven people you're genuinely interested in. That's your entire list — no endless swiping, no algorithmic feed. Small by design.",
  },
  {
    n: "02",
    title: "Go through guided rounds",
    body: "Each round asks you and your matches one meaningful question — about values, communication, chemistry, and goals. You see their answers. They see yours. You decide who moves forward.",
  },
  {
    n: "03",
    title: "Walk away with one real match",
    body: "After five rounds you're left with one person — the one who showed up, answered honestly, and fit best. Not a maybe. One real match you actually know something about.",
  },
];

const faqs = [
  {
    q: "Why only seven people?",
    a: "Most apps give you thousands of options and let the algorithm decide. We flip that — you choose seven people you're actually interested in, and the process helps you figure out which one is the best fit. Fewer choices, better decisions.",
  },
  {
    q: "What exactly happens in the rounds?",
    a: "There are five rounds. Each one surfaces something new — your values, how you communicate, what you want long-term. Both sides answer. Both sides can see. After each round you decide who stays and who doesn't. It moves at your pace.",
  },
  {
    q: "What do I get at the end?",
    a: "One match — someone you've had five real exchanges with. You'll know their values, communication style, and intentions before you ever meet in person. That's the point.",
  },
  {
    q: "Is BoraMoyo free?",
    a: "You can start for free. Premium plans unlock additional features. No pressure to upgrade before you're ready.",
  },
];

export default function Guide() {
  useEffect(() => {
    document.title = "How It Works — Bora Moyo";
    track("guide_view", { page: "/guide" });
  }, []);

  return (
    <div className="bg-[#05070d] min-h-screen text-white">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-52 md:pb-28 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-5">
            How it works
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-tight mb-8">
            A better way to{" "}
            <span className="italic text-gradient-gold">decide.</span>
          </h1>
          <p className="text-white/60 text-lg md:text-xl leading-relaxed max-w-2xl">
            Pick seven people. Go through five guided rounds. End up with one
            real match you actually know something about.
          </p>
        </div>
      </section>

      {/* What is BoraMoyo */}
      <section className="py-16 md:py-20 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            What is BoraMoyo
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-6">
            Dating with structure.
          </h2>
          <div className="space-y-4 text-white/65 text-base md:text-lg leading-relaxed">
            <p>
              BoraMoyo is a guided dating experience designed for people who are
              serious about finding a real connection — not just another match.
            </p>
            <p>
              The name means "good heart" in Swahili. The platform is built
              around that idea: helping you find the person with the best heart
              for you, through a process that rewards intention over impulse.
            </p>
            <p>
              No swiping. No infinite feeds. No algorithmic noise. Just a clear,
              calm process that ends with one person.
            </p>
          </div>
        </div>
      </section>

      {/* Step-by-step */}
      <section className="py-16 md:py-24 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            The journey
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-12">
            Three steps. That's it.
          </h2>
          <div className="space-y-8">
            {steps.map((s) => (
              <div
                key={s.n}
                className="grid grid-cols-[auto_1fr] gap-6 items-start glass rounded-2xl p-6 md:p-7"
              >
                <div className="font-mono text-[#D4AF37] text-sm tracking-[0.2em] pt-0.5">
                  {s.n}
                </div>
                <div>
                  <div className="font-display text-xl md:text-2xl mb-2 leading-tight">
                    {s.title}
                  </div>
                  <p className="text-white/55 text-sm md:text-base leading-relaxed">
                    {s.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 border-t border-white/5 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">
            Common questions
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-12">
            Good to know.
          </h2>
          <div className="space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-white/8 pb-6">
                <div className="font-display text-lg md:text-xl mb-2">{f.q}</div>
                <p className="text-white/55 text-sm md:text-base leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/5 px-6 md:px-12 text-center">
        <div className="mx-auto max-w-xl">
          <h2 className="font-display text-4xl md:text-5xl leading-tight mb-6">
            Ready to begin?
          </h2>
          <p className="text-white/55 text-base mb-10 leading-relaxed">
            Takes about four minutes. Free to start. No commitment required.
          </p>
          <Link
            to="/onboarding"
            data-testid="guide-cta"
            onClick={() => track("guide_cta_clicked", { page: "/guide" })}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-gold text-[#080B14] font-semibold px-8 py-4 glow-gold hover:scale-[1.02] transition-transform"
          >
            Start Your Journey
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
              <path d="M5 12h14m-5-5 5 5-5 5" />
            </svg>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
